import React, { useState } from 'react';
import { Button } from '../components/Button';
import { PhoneIcon, MessageSquareIcon, ClockIcon, CalendarIcon, HelpCircleIcon, BellIcon, CheckIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon, StarIcon, QuoteIcon, ChevronDownIcon } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
function AnimatedSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  return <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-2020">
      {children}
    </div>;
}
function HeroV1Light() {
  return <section className="w-full bg-white text-[#1A1A1A] py-28 md:py-40 lg:py-48 relative overflow-hidden" data-id="element-2021">
      <div className="absolute inset-0" data-id="element-2022">
        <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=80" alt="" className="w-full h-full object-cover" data-id="element-2023" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 via-85% to-white/20" data-id="element-2024"></div>
      </div>

      <div className="max-w-6xl mx-auto px-8 md:px-12 text-center relative z-10" data-id="element-2025">
        <h1 className="font-['Poppins'] font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.15] mb-8 tracking-tight text-[#1A1A1A] animate-[fadeInUp_0.8s_ease-out]" data-id="element-2026">
          Your 24/7 AI Answering Service That Grows Your Business
        </h1>
        <p className="font-['Inter'] text-lg md:text-xl lg:text-2xl text-[#6B6B6B] max-w-4xl mx-auto mb-12 leading-relaxed font-light animate-[fadeInUp_0.8s_ease-out_0.2s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2027">
          Instant call pickup, professional message capture, and a fully branded
          AI agent designed to improve customer satisfaction and support your
          business around the clock.
        </p>
        <div className="animate-[fadeInUp_0.8s_ease-out_0.4s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2028">
          <Button size="lg" data-id="element-2029">Start Free 7-Day Trial</Button>
        </div>
      </div>
    </section>;
}
function ValuePropositionV1Light() {
  const benefits = [{
    icon: ClockIcon,
    title: '24/7 Instant Call Answering',
    description: 'Never miss a call again with round-the-clock AI coverage that picks up instantly'
  }, {
    icon: MessageSquareIcon,
    title: 'Customised Company Greeting',
    description: 'Fully branded experience that sounds like your business, not a generic service'
  }, {
    icon: PhoneIcon,
    title: 'Accurate Capture',
    description: 'Precise name, number, and message recording with intelligent transcription'
  }, {
    icon: BellIcon,
    title: 'Real-Time Call Summaries',
    description: 'Instant notifications with complete call context delivered immediately'
  }, {
    icon: HelpCircleIcon,
    title: 'FAQ Handling',
    description: 'Answer common questions with fenced, brand-safe responses automatically'
  }, {
    icon: CalendarIcon,
    title: 'Appointment Booking',
    description: 'Seamless calendar integration for automated scheduling and confirmations'
  }];
  return <section className="w-full bg-[#F5F5F5] py-28 md:py-36" data-id="element-2030">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2031">
        <AnimatedSection data-id="element-2032">
          <div className="text-center mb-20" data-id="element-2033">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] mb-6 tracking-tight" data-id="element-2034">
              Why Choose Our AI Answering Service?
            </h2>
            <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#6B6B6B]" data-id="element-2035">
              Your Always-On Receptionist, without the overheads
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2036">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <AnimatedSection key={index} delay={index * 100} data-id="element-2037">
                <div className="bg-white rounded-2xl p-10 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md h-full" data-id="element-2038">
                  <div className="w-14 h-14 bg-[#F1B530]/10 rounded-xl flex items-center justify-center mb-6 border border-[#F1B530]/20" data-id="element-2039">
                    <Icon className="w-7 h-7 text-[#F1B530]" data-id="element-2040" />
                  </div>
                  <h3 className="font-['Inter'] font-semibold text-xl text-[#1A1A1A] mb-4" data-id="element-2041">
                    {benefit.title}
                  </h3>
                  <p className="font-['Inter'] text-[#6B6B6B] leading-relaxed font-light" data-id="element-2042">
                    {benefit.description}
                  </p>
                </div>
              </AnimatedSection>;
        })}
        </div>
      </div>
    </section>;
}
function FeatureShowcaseV1Light() {
  return <section className="w-full bg-white py-28 md:py-36" data-id="element-2043">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2044">
        <AnimatedSection data-id="element-2045">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-32" data-id="element-2046">
            <div className="relative" data-id="element-2047">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-xl" data-id="element-2048">
                <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80" alt="Real-time dashboard analytics" className="w-full h-full object-cover" data-id="element-2049" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#F1B530] rounded-2xl p-6 shadow-xl" data-id="element-2050">
                <div className="font-['Poppins'] text-4xl font-bold text-white mb-1" data-id="element-2051">
                  100%
                </div>
                <div className="font-['Inter'] text-sm text-white font-semibold" data-id="element-2052">
                  Capture Rate
                </div>
              </div>
            </div>

            <div data-id="element-2053">
              <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6" data-id="element-2054">
                Real-Time Dashboard & Analytics
              </h2>
              <p className="font-['Inter'] text-lg text-[#6B6B6B] mb-8 leading-relaxed font-light" data-id="element-2055">
                Monitor every call in real-time with comprehensive analytics.
                Track call volume, response times, customer satisfaction, and
                conversion rates all in one intuitive dashboard.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-2056">
                <li className="flex items-start" data-id="element-2057">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2058">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2059">
                    Live call monitoring and notifications
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2060">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2061">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2062">
                    Detailed call transcripts and recordings
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2063">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2064">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2065">
                    Performance metrics and insights
                  </span>
                </li>
              </ul>
              <Button data-id="element-2066">Explore Dashboard</Button>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection data-id="element-2067">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-32" data-id="element-2068">
            <div className="lg:order-2 relative" data-id="element-2069">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-xl" data-id="element-2070">
                <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80" alt="Seamless integrations" className="w-full h-full object-cover" data-id="element-2071" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border-2 border-[#F1B530] rounded-2xl p-6 shadow-xl" data-id="element-2072">
                <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-2073">
                  50+
                </div>
                <div className="font-['Inter'] text-sm text-[#1A1A1A] font-semibold" data-id="element-2074">
                  Integrations
                </div>
              </div>
            </div>

            <div className="lg:order-1" data-id="element-2075">
              <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6" data-id="element-2076">
                Seamless CRM & Tool Integration
              </h2>
              <p className="font-['Inter'] text-lg text-[#6B6B6B] mb-8 leading-relaxed font-light" data-id="element-2077">
                Connect with your existing tools effortlessly. Our AI integrates
                with popular CRMs, calendars, and business tools to keep your
                workflow smooth and data synchronized.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-2078">
                <li className="flex items-start" data-id="element-2079">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2080">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2081">
                    Salesforce, HubSpot, Zoho CRM
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2082">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2083">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2084">
                    Google Calendar, Outlook, Calendly
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2085">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2086">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2087">
                    Slack, Teams, Zapier, and more
                  </span>
                </li>
              </ul>
              <Button data-id="element-2088">View All Integrations</Button>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection data-id="element-2089">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2090">
            <div className="relative" data-id="element-2091">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-xl" data-id="element-2092">
                <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80" alt="AI training and customization" className="w-full h-full object-cover" data-id="element-2093" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white border-2 border-[#F1B530] rounded-2xl p-6 shadow-xl" data-id="element-2094">
                <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-2095">
                  &lt;5min
                </div>
                <div className="font-['Inter'] text-sm text-[#1A1A1A] font-semibold" data-id="element-2096">
                  Setup Time
                </div>
              </div>
            </div>

            <div data-id="element-2097">
              <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6" data-id="element-2098">
                Custom AI Training for Your Business
              </h2>
              <p className="font-['Inter'] text-lg text-[#6B6B6B] mb-8 leading-relaxed font-light" data-id="element-2099">
                Train your AI receptionist with your business knowledge, FAQs,
                and specific protocols. The more you customize, the more natural
                and helpful your AI becomes.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-2100">
                <li className="flex items-start" data-id="element-2101">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2102">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2103">
                    Upload FAQs and knowledge base
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2104">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2105">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2106">
                    Define custom call routing rules
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2107">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-2108">•</span>
                  <span className="font-['Inter'] text-[#1A1A1A] font-light" data-id="element-2109">
                    Set brand voice and tone guidelines
                  </span>
                </li>
              </ul>
              <Button data-id="element-2110">Start Customizing</Button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}
function IndustryBenefitsV1Light() {
  const industries = [{
    name: 'Healthcare',
    benefits: ['HIPAA compliant', 'Appointment scheduling', 'Emergency routing', 'Patient messaging']
  }, {
    name: 'Legal',
    benefits: ['Client intake', 'Confidential handling', 'Urgent call screening', 'Case management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiries', 'Viewing bookings', 'Lead capture', 'Tenant support']
  }, {
    name: 'Trade Services',
    benefits: ['Job booking', 'Emergency calls', 'Quote requests', 'Service scheduling']
  }, {
    name: 'Retail',
    benefits: ['Order taking', 'Store inquiries', 'Stock checks', 'Customer support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening', 'Appointment booking', 'Message handling', 'Consultation scheduling']
  }];
  return <section className="w-full bg-[#F5F5F5] py-28" data-id="element-2111">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2112">
        <AnimatedSection data-id="element-2113">
          <div className="text-center mb-16" data-id="element-2114">
            <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#1A1A1A] mb-6" data-id="element-2115">
              Trusted Across Industries
            </h2>
            <p className="font-['Inter'] text-xl text-[#6B6B6B] font-light" data-id="element-2116">
              Tailored solutions for every business type
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2117">
          {industries.map((industry, index) => <AnimatedSection key={index} delay={index * 100} data-id="element-2118">
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 hover:border-[#F1B530]/50 transition-all duration-300 shadow-sm hover:shadow-md h-full" data-id="element-2119">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-6" data-id="element-2120">
                  {industry.name}
                </h3>
                <ul className="space-y-3" data-id="element-2121">
                  {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#6B6B6B] flex items-start" data-id="element-2122">
                      <span className="text-[#F1B530] mr-2" data-id="element-2123">•</span>
                      {benefit}
                    </li>)}
                </ul>
              </div>
            </AnimatedSection>)}
        </div>
      </div>
    </section>;
}
function PricingPlansV1Light() {
  const plans = [{
    name: 'Scout 25',
    price: 'AUD $65',
    calls: '25 calls/month',
    minutes: '125',
    target: 'Perfect for solo operators testing the waters',
    inclusions: ['Instant call pickup', 'Customised company greeting', 'Name, number & message capture', 'Real-time call summaries via SMS/email', 'Call recordings & transcripts', 'Dashboard analytics', 'Excess: $2.60/call or $0.52/min'],
    ctaText: 'Start 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Forager 50',
    price: 'AUD $115',
    calls: '50 calls/month',
    minutes: '250',
    target: 'Small teams with steady enquiry flow',
    inclusions: ['Everything in Scout 25', 'Priority support', 'Extended call handling', 'Multi-user dashboard access', 'Custom call routing rules', 'Excess: $2.30/call or $0.46/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording',
    isPopular: true
  }, {
    name: 'Drift 75',
    price: 'AUD $165',
    calls: '75 calls/month',
    minutes: '375',
    target: 'Growing teams with rising call peaks',
    inclusions: ['Everything in Forager 50', 'Advanced analytics', 'Team collaboration tools', 'Custom integrations', 'Dedicated account manager', 'Excess: $2.20/call or $0.44/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Surge 150',
    price: 'AUD $325',
    calls: '150 calls/month',
    minutes: '750',
    target: 'High-tempo operations with regular spikes',
    inclusions: ['Everything in Drift 75', 'Priority call routing', 'Advanced workflow automation', 'Custom reporting', 'API access', 'Excess: $2.17/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Swarm 300',
    price: 'AUD $645',
    calls: '300 calls/month',
    minutes: '1,500',
    target: 'Heavy daily call activity teams',
    inclusions: ['Everything in Surge 150', 'Enterprise-grade security', 'Custom AI training', 'White-label options', 'SLA guarantees', 'Excess: $2.15/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Colony 500',
    price: 'AUD $955',
    calls: '500 calls/month',
    minutes: '2,250',
    target: 'Enterprise-level high-volume operations',
    inclusions: ['Everything in Swarm 300', 'Dedicated infrastructure', 'Custom AI models', 'Advanced compliance features', '24/7 premium support', 'Excess: $1.91/call or $0.42/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'AI Automation Customisation',
    target: 'For businesses requiring advanced automation and integrations beyond standard plans',
    inclusions: ['Custom call volumes', 'Bespoke AI training', 'Enterprise integrations', 'Advanced workflow automation', 'Dedicated success team', 'Custom SLAs'],
    ctaText: 'Contact Sales',
    isCustom: true
  }];
  return <section className="w-full bg-white py-28 md:py-36" data-id="element-2124">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2125">
        <AnimatedSection data-id="element-2126">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#1A1A1A] text-center mb-20 tracking-tight" data-id="element-2127">
            Plans
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-2128">
          {plans.slice(0, 6).map((plan, index) => <AnimatedSection key={index} delay={index * 100} data-id="element-2129">
              <div className={`bg-white rounded-2xl p-10 border transition-all duration-300 hover:-translate-y-1 relative flex flex-col h-full shadow-sm hover:shadow-md ${plan.isPopular ? 'border-[#F1B530] shadow-[0_0_40px_rgba(241,181,48,0.15)]' : 'border-[#E5E5E5] hover:border-[#F1B530]/50'}`} data-id="element-2130">
                {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-white px-6 py-2 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-2131">
                    Most Popular
                  </div>}

                <div className="mb-8" data-id="element-2132">
                  <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-3" data-id="element-2133">
                    {plan.name}
                  </h3>
                  {!plan.isCustom ? <>
                      <div className="mb-3" data-id="element-2134">
                        <span className="font-['Poppins'] font-bold text-5xl text-[#1A1A1A]" data-id="element-2135">
                          {plan.price}
                        </span>
                        <span className="text-[#6B6B6B] font-['Inter'] ml-2 text-sm" data-id="element-2136">
                          plus GST/month
                        </span>
                      </div>
                      <p className="text-[#6B6B6B] font-['Inter'] text-sm mb-2 font-light" data-id="element-2137">
                        Up to {plan.calls} • {plan.minutes} free minutes
                      </p>
                    </> : <div className="mb-3" data-id="element-2138">
                      <span className="font-['Poppins'] font-bold text-4xl text-[#1A1A1A]" data-id="element-2139">
                        Custom Pricing
                      </span>
                    </div>}
                  <p className="text-[#6B6B6B] font-['Inter'] text-sm italic font-light" data-id="element-2140">
                    {plan.target}
                  </p>
                </div>

                <div className="mb-10 flex-grow" data-id="element-2141">
                  <ul className="space-y-4" data-id="element-2142">
                    {plan.inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-2143">
                        <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-2144" />
                        <span className="font-['Inter'] text-[#1A1A1A] text-sm leading-relaxed font-light" data-id="element-2145">
                          {inclusion}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div className="mt-auto" data-id="element-2146">
                  <Button className="w-full" size="lg" data-id="element-2147">
                    {plan.ctaText}
                  </Button>

                  {plan.ctaLink && <a href="#" className="block text-center mt-5 text-[#6B6B6B] font-['Inter'] text-sm hover:text-[#F1B530] transition-colors duration-300" data-id="element-2148">
                      {plan.ctaLink}
                    </a>}
                </div>
              </div>
            </AnimatedSection>)}
        </div>

        <AnimatedSection delay={600} data-id="element-2149">
          <div className="max-w-2xl mx-auto" data-id="element-2150">
            <div className="bg-white rounded-2xl p-10 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full shadow-sm hover:shadow-md" data-id="element-2151">
              <div className="mb-8" data-id="element-2152">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-3" data-id="element-2153">
                  {plans[6].name}
                </h3>
                <div className="mb-3" data-id="element-2154">
                  <span className="font-['Poppins'] font-bold text-4xl text-[#1A1A1A]" data-id="element-2155">
                    Custom Pricing
                  </span>
                </div>
                <p className="text-[#6B6B6B] font-['Inter'] text-sm italic font-light" data-id="element-2156">
                  {plans[6].target}
                </p>
              </div>

              <div className="mb-10 flex-grow" data-id="element-2157">
                <ul className="space-y-4" data-id="element-2158">
                  {plans[6].inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-2159">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-2160" />
                      <span className="font-['Inter'] text-[#1A1A1A] text-sm leading-relaxed font-light" data-id="element-2161">
                        {inclusion}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-auto" data-id="element-2162">
                <Button className="w-full" size="lg" data-id="element-2163">
                  {plans[6].ctaText}
                </Button>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}
function TestimonialsV1Light() {
  const testimonials = [{
    quote: "This AI answering service has transformed how we handle customer calls. We've seen a 40% increase in captured leads since implementing it.",
    author: 'Sarah Mitchell',
    role: 'CEO, TechStart Solutions',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    rating: 5
  }, {
    quote: 'The 24/7 coverage means we never miss an opportunity. Our customers love the instant response, and so do we.',
    author: 'James Chen',
    role: 'Operations Director, GrowthCo',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
    rating: 5
  }, {
    quote: "Setup was incredibly easy, and the AI sounds so natural. It's like having a professional receptionist without the overhead costs.",
    author: 'Emma Thompson',
    role: 'Founder, Bright Ideas Agency',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80',
    rating: 5
  }];
  return <section className="w-full bg-[#F5F5F5] py-28 md:py-36" data-id="element-2164">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2165">
        <AnimatedSection data-id="element-2166">
          <div className="text-center mb-20" data-id="element-2167">
            <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#1A1A1A] mb-6" data-id="element-2168">
              Trusted by Growing Businesses
            </h2>
            <p className="font-['Inter'] text-xl text-[#6B6B6B] font-light" data-id="element-2169">
              See what our customers have to say
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2170">
          {testimonials.map((testimonial, index) => <AnimatedSection key={index} delay={index * 150} data-id="element-2171">
              <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 hover:border-[#F1B530]/50 transition-all duration-300 shadow-sm hover:shadow-md h-full" data-id="element-2172">
                <div className="flex gap-1 mb-6" data-id="element-2173">
                  {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-2174" />)}
                </div>

                <QuoteIcon className="w-10 h-10 text-[#F1B530]/20 mb-4" data-id="element-2175" />

                <p className="font-['Inter'] text-[#1A1A1A] mb-8 leading-relaxed font-light" data-id="element-2176">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center gap-4" data-id="element-2177">
                  <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover border-2 border-[#F1B530]/20" data-id="element-2178" />
                  <div data-id="element-2179">
                    <div className="font-['Inter'] font-semibold text-[#1A1A1A]" data-id="element-2180">
                      {testimonial.author}
                    </div>
                    <div className="font-['Inter'] text-sm text-[#6B6B6B]" data-id="element-2181">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>)}
        </div>
      </div>
    </section>;
}
function AddOnsV1Light() {
  const addOns = [{
    icon: PhoneForwardedIcon,
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant Escalation for Time Sensitive Opportunities', 'Transfers With Full Caller Context', 'Smart Routing Based on Your Real Business Rules']
  }, {
    icon: MessageCircleIcon,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Turn AI agent into trained front-line knowledge assistant', 'Fenced responses using only approved information', 'Clear brand-safe responses', 'Faster resolution & reduced message volume']
  }, {
    icon: CalendarCheckIcon,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80',
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Real-time calendar syncing (Google, Outlook, etc.)', 'Automated booking and confirmation', 'Custom booking rules', 'Caller notifications and internal alerts']
  }];
  return <section className="w-full bg-white py-28 md:py-36" data-id="element-2182">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2183">
        <AnimatedSection data-id="element-2184">
          <div className="text-center mb-20" data-id="element-2185">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6 tracking-tight" data-id="element-2186">
              Universal Add-Ons
            </h2>
            <p className="font-['Inter'] text-lg text-[#6B6B6B] max-w-3xl mx-auto font-light" data-id="element-2187">
              Available for all plans to extend your AI answering service
              capabilities
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2188">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <AnimatedSection key={index} delay={index * 150} data-id="element-2189">
                <div className="bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-md h-full" data-id="element-2190">
                  <div className="relative h-48 overflow-hidden" data-id="element-2191">
                    <img src={addOn.image} alt={addOn.name} className="w-full h-full object-cover opacity-80" data-id="element-2192" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" data-id="element-2193"></div>
                    <div className="absolute bottom-4 left-4 w-14 h-14 bg-[#F1B530]/90 backdrop-blur-sm rounded-xl flex items-center justify-center border border-[#F1B530]" data-id="element-2194">
                      <Icon className="w-7 h-7 text-white" data-id="element-2195" />
                    </div>
                  </div>

                  <div className="p-10" data-id="element-2196">
                    <h3 className="font-['Poppins'] font-bold text-2xl text-[#1A1A1A] mb-3" data-id="element-2197">
                      {addOn.name}
                    </h3>

                    <div className="mb-8" data-id="element-2198">
                      <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-2199">
                        {addOn.price}
                      </span>
                      <span className="text-[#6B6B6B] font-['Inter'] text-sm ml-2" data-id="element-2200">
                        {addOn.setup}
                      </span>
                    </div>

                    <ul className="space-y-4" data-id="element-2201">
                      {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-2202">
                          <span className="text-[#F1B530] mr-3 flex-shrink-0 text-lg" data-id="element-2203">
                            •
                          </span>
                          <span className="font-['Inter'] text-[#1A1A1A] text-sm leading-relaxed font-light" data-id="element-2204">
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </AnimatedSection>;
        })}
        </div>
      </div>
    </section>;
}
function AdvancedAutomationV1Light() {
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Route leads intelligently'
  }, {
    icon: ZapIcon,
    text: 'Trigger automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Sync directly into CRMs'
  }, {
    icon: BrainCircuitIcon,
    text: 'Collect structured information'
  }];
  return <section className="w-full bg-[#F5F5F5] py-28 md:py-36 relative overflow-hidden" data-id="element-2205">
      <div className="absolute inset-0" data-id="element-2206">
        <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80" alt="" className="w-full h-full object-cover opacity-[0.05]" data-id="element-2207" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5F5F5]/95 via-[#F5F5F5]/90 to-[#F5F5F5]" data-id="element-2208"></div>
      </div>

      <div className="max-w-6xl mx-auto px-8 md:px-12 text-center relative z-10" data-id="element-2209">
        <AnimatedSection data-id="element-2210">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight text-[#1A1A1A]" data-id="element-2211">
            AI Automation Customisation Advanced
          </h2>
          <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#6B6B6B] mb-10" data-id="element-2212">
            Automate More Than Calls. Automate Your Entire Frontline.
          </p>

          <p className="font-['Inter'] text-lg text-[#6B6B6B] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-2213">
            For businesses handling 500+ calls per month, unlock advanced
            capabilities that transform your AI answering service into a
            complete frontline automation platform.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-2214">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <AnimatedSection key={index} delay={index * 100} data-id="element-2215">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-[#E5E5E5] hover:border-[#F1B530]/50 transition-all duration-300 shadow-sm hover:shadow-md h-full flex flex-col items-center justify-center min-h-[180px]" data-id="element-2216">
                  <Icon className="w-10 h-10 text-[#F1B530] mx-auto mb-5" data-id="element-2217" />
                  <p className="font-['Inter'] text-[#1A1A1A] font-light text-center" data-id="element-2218">
                    {capability.text}
                  </p>
                </div>
              </AnimatedSection>;
        })}
        </div>

        <AnimatedSection delay={400} data-id="element-2219">
          <div className="mb-16 relative" data-id="element-2220">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-[#E5E5E5] max-w-4xl mx-auto shadow-lg" data-id="element-2221">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80" alt="Automation workflow visualization" className="w-full h-64 object-cover rounded-xl opacity-90" data-id="element-2222" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-2223">
                <div className="bg-[#F1B530]/10 backdrop-blur-md border border-[#F1B530]/30 rounded-xl px-8 py-4" data-id="element-2224">
                  <p className="font-['Inter'] text-[#F1B530] font-semibold text-lg" data-id="element-2225">
                    Complete Workflow Automation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={500} data-id="element-2226">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-10 border border-[#E5E5E5] mb-12 max-w-3xl mx-auto shadow-sm" data-id="element-2227">
            <h3 className="font-['Inter'] font-semibold text-xl mb-6 text-[#1A1A1A]" data-id="element-2228">
              Extended Capabilities Include:
            </h3>
            <ul className="font-['Inter'] text-[#6B6B6B] space-y-3 text-left max-w-2xl mx-auto font-light" data-id="element-2229">
              <li data-id="element-2230">• Support multi-step decision trees</li>
              <li data-id="element-2231">• Extended conversational capabilities</li>
              <li data-id="element-2232">• Custom API integrations</li>
              <li data-id="element-2233">• Advanced data collection and processing</li>
              <li data-id="element-2234">• Bespoke workflow automation</li>
            </ul>
          </div>

          <Button size="lg" data-id="element-2235">Contact Sales</Button>
        </AnimatedSection>
      </div>
    </section>;
}
function FAQV1Light() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7, captures caller information, handles FAQs, and can even book appointments. It integrates seamlessly with your existing systems and can be customized to match your business needs.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely! You can fully customize your company greeting, upload FAQs, set routing rules, and define your brand voice. The AI learns from your business to provide accurate, on-brand responses.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: 'The AI can transfer calls to your team with full context when needed. You set the rules for when calls should be escalated, ensuring important matters always reach the right person.'
  }, {
    question: 'How quickly can I get started?',
    answer: "Setup takes less than 5 minutes. Simply sign up, customize your greeting and settings, forward your number, and you're live. We provide step-by-step guidance throughout the process."
  }, {
    question: 'Is there a contract or commitment?',
    answer: 'No long-term contracts required. Start with our 7-day free trial, then choose a monthly plan that fits your needs. You can upgrade, downgrade, or cancel anytime.'
  }];
  return <section className="w-full bg-white py-28 md:py-36" data-id="element-2236">
      <div className="max-w-4xl mx-auto px-8 md:px-12" data-id="element-2237">
        <AnimatedSection data-id="element-2238">
          <div className="text-center mb-16" data-id="element-2239">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#1A1A1A] mb-6" data-id="element-2240">
              Frequently Asked Questions
            </h2>
            <p className="font-['Inter'] text-lg text-[#6B6B6B] font-light" data-id="element-2241">
              Everything you need to know about our AI answering service
            </p>
          </div>
        </AnimatedSection>

        <div className="space-y-4" data-id="element-2242">
          {faqs.map((faq, index) => <AnimatedSection key={index} delay={index * 100} data-id="element-2243">
              <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/50 shadow-sm hover:shadow-md" data-id="element-2244">
                <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-2245">
                  <span className="font-['Inter'] font-semibold text-xl text-[#1A1A1A] pr-8" data-id="element-2246">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-2247" />
                </button>

                <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-2248">
                  <div className="px-8 pb-6" data-id="element-2249">
                    <p className="font-['Inter'] text-[#6B6B6B] leading-relaxed font-light" data-id="element-2250">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>)}
        </div>
      </div>
    </section>;
}
export function LandingPageV1Light() {
  return <div className="w-full min-h-screen bg-white" data-id="element-2251">
      <style data-id="element-2252">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <HeroV1Light data-id="element-2253" />
      <ValuePropositionV1Light data-id="element-2254" />
      <FeatureShowcaseV1Light data-id="element-2255" />
      <IndustryBenefitsV1Light data-id="element-2256" />
      <PricingPlansV1Light data-id="element-2257" />
      <TestimonialsV1Light data-id="element-2258" />
      <AddOnsV1Light data-id="element-2259" />
      <AdvancedAutomationV1Light data-id="element-2260" />
      <FAQV1Light data-id="element-2261" />
    </div>;
}